import { Box, Button, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { isValid, parseISO } from 'date-fns';
import { SimilarCandidateResult } from 'lib/Model/SimilarCandidates';
import * as React from 'react';
import { Feedback } from './FeedbackControls';
import { ResultCard } from './ResultCard';

// How the list is ordered. 'score' is the default match ranking; 'activity'
// orders by CV date (v7+), with candidates that have no cv_date sorted to the
// end and ties broken by match score.
export type SortMode = 'score' | 'activity';

function activityTime(value?: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed.getTime() : Number.NEGATIVE_INFINITY;
}

// Tier guardrails. The cut between "strong" and "additional" is gap-based (the
// largest drop in final score), but bounded so a noisy run can't produce a
// misleading tier: the strong tier never exceeds STRONG_TIER_CAP, never includes
// scores below STRONG_SCORE_FLOOR, and only splits at a gap that is actually
// meaningful (>= SIGNIFICANT_GAP). When scores are uniformly close — the
// near-tie case we're solving for — the whole eligible head stays as one set.
const STRONG_TIER_CAP = 10;
const STRONG_SCORE_FLOOR = 0.5;
const SIGNIFICANT_GAP = 0.07;

export function splitTiers(results: SimilarCandidateResult[]): {
  strong: SimilarCandidateResult[];
  additional: SimilarCandidateResult[];
} {
  const sorted = [...results].sort((a, b) => b.final_score - a.final_score);

  // Eligible head: above the floor, capped — sorted desc, so stop at first miss.
  const head: SimilarCandidateResult[] = [];
  for (const r of sorted) {
    if (head.length >= STRONG_TIER_CAP) break;
    if (r.final_score < STRONG_SCORE_FLOOR) break;
    head.push(r);
  }

  // Cut at the largest gap within the head, but only if it's meaningful.
  let cutIdx = head.length;
  let maxGap = 0;
  for (let i = 0; i < head.length - 1; i += 1) {
    const gap = head[i].final_score - head[i + 1].final_score;
    if (gap > maxGap) {
      maxGap = gap;
      cutIdx = i + 1;
    }
  }
  const strong = maxGap >= SIGNIFICANT_GAP ? head.slice(0, cutIdx) : head;

  const strongIds = new Set(strong.map((r) => r.candidate_id));
  const additional = sorted.filter((r) => !strongIds.has(r.candidate_id));

  return { strong, additional };
}

// Results shown before "Show more". Ranks 1-10 run ~71-79% good in recruiter
// feedback; ranks 11+ drop to ~44%, so the coin-flip tail is one click away
// rather than filling the default page. All 20 remain accessible.
const INITIAL_VISIBLE_RESULTS = 10;

// At or below this rerank score (3/10) the matcher prompts skip the reason, so
// these cards stay behind "Show more" even inside the top 10. Keep in step with
// the vacancy/candidate rerank prompts in intranet-api.
export const WEAK_LLM_SCORE = 0.3;

const useStyles = makeStyles((theme) => ({
  showMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
  },
  group: {
    display: 'grid',
    // Expanded cards need enough width for the profile preview to remain legible.
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    // Stretch so both cards in a row share the taller height — variable reason
    // length and chip count would otherwise leave neighbours visibly uneven.
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

interface Props {
  results: SimilarCandidateResult[];
  feedback: Record<string, Feedback | undefined>;
  onFeedbackChange: (id: string, next: Feedback | undefined) => void;
  onOpen?: (id: string) => void;
  sortBy?: SortMode;
  defaultExpanded?: boolean;
  firstCardRef?: (el: HTMLDivElement | null) => void;
  tutorialFocus?: 'card' | 'score' | 'details' | 'reason' | 'feedback';
  hideWeak?: boolean;
}

export const ResultsList: React.FC<Props> = ({
  results,
  feedback,
  onFeedbackChange,
  onOpen,
  sortBy = 'score',
  defaultExpanded = false,
  firstCardRef,
  tutorialFocus,
  hideWeak = false,
}) => {
  const classes = useStyles();

  // One continuous, ranked list: ranking is the only structure. A tiered
  // "strongest vs additional" split made recruiters stop at the top group and
  // never review the rest, so every result is numbered and rendered together.
  // Default order is by match score; sorting by activity falls back to score
  // for candidates with no activity date and to break ties.
  const sorted = React.useMemo(() => {
    const byScore = (a: SimilarCandidateResult, b: SimilarCandidateResult) =>
      b.final_score - a.final_score;
    if (sortBy === 'activity') {
      return [...results].sort((a, b) => {
        const diff = activityTime(b.cv_date) - activityTime(a.cv_date);
        return diff !== 0 ? diff : byScore(a, b);
      });
    }
    return [...results].sort(byScore);
  }, [results, sortBy]);

  // Keep one continuous list, but still flag the strongest cluster inline:
  // splitTiers finds the gap-based head, and those cards get a "Strongest" tag
  // rather than being broken out into their own section.
  const strongIds = React.useMemo(
    () => new Set(splitTiers(results).strong.map((r) => r.candidate_id)),
    [results],
  );

  // Collapse the tail again whenever a new result set arrives.
  const [showAll, setShowAll] = React.useState(false);
  React.useEffect(() => {
    setShowAll(false);
  }, [results]);
  let visible = sorted;
  if (!showAll) {
    visible = sorted
      .slice(0, INITIAL_VISIBLE_RESULTS)
      .filter(
        (r) => !hideWeak || r.llm_score == null || r.llm_score > WEAK_LLM_SCORE,
      );
  }
  const hiddenCount = sorted.length - visible.length;

  const cardEls = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const prevRects = React.useRef<Map<string, DOMRect>>(new Map());

  React.useLayoutEffect(() => {
    cardEls.current.forEach((el, id) => {
      const newRect = el.getBoundingClientRect();
      const prev = prevRects.current.get(id);
      if (prev) {
        const dy = prev.top - newRect.top;
        const dx = prev.left - newRect.left;
        if (Math.abs(dy) > 0.5 || Math.abs(dx) > 0.5) {
          el.style.transition = 'none';
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          requestAnimationFrame(() => {
            el.style.transition = 'transform 450ms cubic-bezier(0.2, 0, 0, 1)';
            el.style.transform = '';
          });
        }
      }
      prevRects.current.set(id, newRect);
    });
  });

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      cardEls.current.set(id, el);
    } else {
      cardEls.current.delete(id);
      prevRects.current.delete(id);
    }
  };

  const renderCard = (result: SimilarCandidateResult, rank: number) => (
    <ResultCard
      key={result.candidate_id}
      cardRef={(el) => {
        setRef(result.candidate_id)(el);
        if (rank === 1) firstCardRef?.(el);
      }}
      rank={rank}
      isStrong={strongIds.has(result.candidate_id)}
      result={result}
      finalScore={result.final_score}
      feedback={feedback[result.candidate_id]}
      onFeedbackChange={(next) => onFeedbackChange(result.candidate_id, next)}
      onOpen={onOpen ? () => onOpen(result.candidate_id) : undefined}
      defaultExpanded={defaultExpanded}
      tutorialFocus={rank === 1 ? tutorialFocus : undefined}
    />
  );

  return (
    <>
      <Box className={classes.group}>
        {/* rank from the full list so it doesn't shift when weak cards show */}
        {visible.map((result) =>
          renderCard(result, sorted.indexOf(result) + 1),
        )}
      </Box>
      {hiddenCount > 0 && (
        <Box className={classes.showMoreRow}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            endIcon={<ExpandMoreIcon />}
            onClick={() => setShowAll(true)}
          >
            Show {hiddenCount} more{' '}
            {hiddenCount === 1 ? 'candidate' : 'candidates'}
          </Button>
        </Box>
      )}
    </>
  );
};
